import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { UserPlus, CheckCircle, AlertCircle, LogIn } from "lucide-react";

export default function InviteAcceptPage() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading | login_required | ready | success | error
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setStatus("login_required");
    } else {
      setStatus("ready");
    }
  }, [user]);

  const handleAccept = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(`/org/invitations/${token}/accept`);
      setResult(data);
      setStatus("success");
    } catch (err) {
      setError(formatApiError(err));
      setStatus("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F5F5] px-4" data-testid="invite-accept-page">
      <div className="w-full max-w-sm text-center space-y-6">
        {status === "login_required" && (
          <>
            <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center mx-auto">
              <LogIn size={24} className="text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#111111]">Sign in to accept</h1>
              <p className="text-sm text-zinc-500 mt-1">You need to be signed in to accept this invitation.</p>
            </div>
            <Button onClick={() => navigate("/login")} className="w-full" data-testid="invite-login-btn">
              Sign in
            </Button>
          </>
        )}

        {status === "ready" && (
          <>
            <div className="w-14 h-14 rounded-xl bg-green-50 flex items-center justify-center mx-auto">
              <UserPlus size={24} className="text-green-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#111111]">Team invitation</h1>
              <p className="text-sm text-zinc-500 mt-1">
                You've been invited to join a team. Click below to accept.
              </p>
            </div>
            <Button
              onClick={handleAccept}
              disabled={loading}
              className="w-full bg-white hover:bg-zinc-800 text-white"
              data-testid="invite-accept-btn"
            >
              {loading ? "Accepting..." : "Accept invitation"}
            </Button>
          </>
        )}

        {status === "success" && (
          <>
            <div className="w-14 h-14 rounded-xl bg-green-50 flex items-center justify-center mx-auto">
              <CheckCircle size={24} className="text-green-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#111111]">Welcome to the team!</h1>
              <p className="text-sm text-zinc-500 mt-1">
                You've joined as <span className="font-medium capitalize">{result?.role}</span>.
              </p>
            </div>
            <Button onClick={() => navigate("/team")} className="w-full" data-testid="invite-go-team-btn">
              Go to team page
            </Button>
          </>
        )}

        {status === "error" && (
          <>
            <div className="w-14 h-14 rounded-xl bg-red-50 flex items-center justify-center mx-auto">
              <AlertCircle size={24} className="text-red-500" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-[#111111]">Something went wrong</h1>
              <p className="text-sm text-red-600 mt-1">{error}</p>
            </div>
            <Link to="/inbox">
              <Button variant="outline" className="w-full" data-testid="invite-back-btn">
                Back to inbox
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
